import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { zoneLevelToApi } from 'pages/Operations/Safety/Permits/permitsData'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllPermitsSingleResponse,
  IGetAllPermitTypesSingleResponse,
  IGetSinglePermitResponse,
  IGetSinglePermitTypeResponse,
  IPermitPostRequest,
  IPermitStatusPatchRequest,
  IPermitTypePatchRequest,
  IPermitTypePostRequest,
} from 'types/api/operationsPermitsApi.interface'
import { IPermitFormState, IPermitsSliceInitialState } from 'types/permits.interface'
import { IPermitTypeFormState, IPermitTypesSliceInitialState } from 'types/permitTypes.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

const mapPermitTypeToForm = (data: IGetSinglePermitTypeResponse): IPermitTypeFormState => ({
  id: data.id,
  code: data.code,
  name: data.name,
  requiresCoPresence: data.requiresCoPresence,
  minHolders: data.minHolders,
  requiredCapabilities: data.requiredCapabilities.map((cap) => ({
    label: cap.name,
    value: cap.id,
  })),
})

const mapPermitTypeFormToPost = (state: IPermitTypeFormState): IPermitTypePostRequest => ({
  code: state.code,
  name: state.name,
  requiresCoPresence: state.requiresCoPresence,
  minHolders: Number(state.minHolders),
  requiredCapabilityIds: state.requiredCapabilities.map((cap) => cap.value),
})

const mapPermitTypeFormToPatch = (state: IPermitTypeFormState): IPermitTypePatchRequest => ({
  code: state.code,
  name: state.name,
  requiresCoPresence: state.requiresCoPresence,
  minHolders: Number(state.minHolders),
  requiredCapabilityIds: state.requiredCapabilities.map((cap) => cap.value),
})

const mapPermitFormToPost = (state: IPermitFormState): IPermitPostRequest => {
  const req: IPermitPostRequest = {
    permitTypeId: state.permitType?.value,
    holderUserIds: state.holderUserIds.map((holder) => holder.value),
  }

  if (state.zoneScopeLevel?.value && state.zoneScopes.length) {
    req.zoneScope = {
      level: zoneLevelToApi[state.zoneScopeLevel.value],
      ids: state.zoneScopes.map((zone) => zone.value),
    }
  }

  if (state.expiresAt) {
    req.expiresAt = state.expiresAt.toISOString()
  }

  return req
}

const permitsInitialState: IPermitsSliceInitialState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singlePermit: null,
}

const permitTypesInitialState: IPermitTypesSliceInitialState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singlePermitType: null,
}

export const getPermits = createAsyncThunk('permits/getPermits', async (params: any, thunkAPI) => {
  try {
    const resp: AxiosResponse<IPaginatedResponse<IGetAllPermitsSingleResponse>> = await baseUrl.get(
      'aim-operations/permit',
      { params },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getSinglePermit = createAsyncThunk(
  'permits/getSinglePermit',
  async (id: string, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IGetSinglePermitResponse> = await baseUrl.get(
        'aim-operations/permit/' + id,
      )
      return data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createPermit = createAsyncThunk(
  'permits/createPermit',
  async (state: IPermitFormState, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetSinglePermitResponse> = await baseUrl.post(
        'aim-operations/permit',
        mapPermitFormToPost(state),
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updatePermitStatus = createAsyncThunk(
  'permits/updatePermitStatus',
  async ({ id, req }: { id: number; req: IPermitStatusPatchRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetSinglePermitResponse> = await baseUrl.patch(
        `aim-operations/permit/${id}/status`,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getPermitTypes = createAsyncThunk(
  'permits/getPermitTypes',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllPermitTypesSingleResponse>> =
        await baseUrl.get('aim-operations/permit-type', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSinglePermitType = createAsyncThunk(
  'permits/getSinglePermitType',
  async (id: string, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IGetSinglePermitTypeResponse> = await baseUrl.get(
        'aim-operations/permit-type/' + id,
      )
      return mapPermitTypeToForm(data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createPermitType = createAsyncThunk(
  'permits/createPermitType',
  async (state: IPermitTypeFormState, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        'aim-operations/permit-type',
        mapPermitTypeFormToPost(state),
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updatePermitType = createAsyncThunk(
  'permits/updatePermitType',
  async ({ id, state }: { id: number; state: IPermitTypeFormState }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.patch(
        `aim-operations/permit-type/${id}`,
        mapPermitTypeFormToPatch(state),
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

interface IPermitsState {
  permits: IPermitsSliceInitialState
  permitTypes: IPermitTypesSliceInitialState
}

const initialState: IPermitsState = {
  permits: permitsInitialState,
  permitTypes: permitTypesInitialState,
}

const permitsSlice = createSlice({
  name: 'permits',
  initialState,
  reducers: {
    clearPermits: (state) => {
      state.permits.data = []
      state.permits.totalCount = 0
      state.permits.totalPages = 0
    },
    clearSinglePermit: (state) => {
      state.permits.singlePermit = null
    },
    clearPermitTypes: (state) => {
      state.permitTypes.data = []
      state.permitTypes.totalCount = 0
      state.permitTypes.totalPages = 0
    },
    clearSinglePermitType: (state) => {
      state.permitTypes.singlePermitType = null
    },
  },
  extraReducers: {
    [getPermits.pending.type]: (state) => {
      state.permits.isLoading = true
    },
    [getPermits.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllPermitsSingleResponse> },
    ) => {
      state.permits.isLoading = false
      state.permits.data = payload.data
      state.permits.totalCount = payload.totalCount
      state.permits.totalPages = payload.totalPages
    },
    [getPermits.rejected.type]: (state, { payload }) => {
      state.permits.isLoading = false
      handleErrors(payload)
    },
    [getSinglePermit.pending.type]: (state) => {
      state.permits.isLoading = true
    },
    [getSinglePermit.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSinglePermitResponse },
    ) => {
      state.permits.isLoading = false
      state.permits.singlePermit = payload
    },
    [getSinglePermit.rejected.type]: (state, { payload }) => {
      state.permits.isLoading = false
      handleErrors(payload)
    },
    [createPermit.pending.type]: (state) => {
      state.permits.isLoading = true
    },
    [createPermit.fulfilled.type]: (state) => {
      state.permits.isLoading = false
      toast.success('Permit created successfully')
    },
    [createPermit.rejected.type]: (state, { payload }) => {
      state.permits.isLoading = false
      handleErrors(payload)
    },
    [updatePermitStatus.pending.type]: (state) => {
      state.permits.isLoading = true
    },
    [updatePermitStatus.fulfilled.type]: (state) => {
      state.permits.isLoading = false
      toast.success('Permit updated successfully')
    },
    [updatePermitStatus.rejected.type]: (state, { payload }) => {
      state.permits.isLoading = false
      handleErrors(payload)
    },
    [getPermitTypes.pending.type]: (state) => {
      state.permitTypes.isLoading = true
    },
    [getPermitTypes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllPermitTypesSingleResponse> },
    ) => {
      state.permitTypes.isLoading = false
      state.permitTypes.data = payload.data
      state.permitTypes.totalCount = payload.totalCount
      state.permitTypes.totalPages = payload.totalPages
    },
    [getPermitTypes.rejected.type]: (state, { payload }) => {
      state.permitTypes.isLoading = false
      handleErrors(payload)
    },
    [getSinglePermitType.pending.type]: (state) => {
      state.permitTypes.isLoading = true
    },
    [getSinglePermitType.fulfilled.type]: (
      state,
      { payload }: { payload: IPermitTypeFormState },
    ) => {
      state.permitTypes.isLoading = false
      state.permitTypes.singlePermitType = payload
    },
    [getSinglePermitType.rejected.type]: (state, { payload }) => {
      state.permitTypes.isLoading = false
      handleErrors(payload)
    },
    [createPermitType.pending.type]: (state) => {
      state.permitTypes.isLoading = true
    },
    [createPermitType.fulfilled.type]: (state) => {
      state.permitTypes.isLoading = false
      toast.success('Permit type created successfully')
    },
    [createPermitType.rejected.type]: (state, { payload }) => {
      state.permitTypes.isLoading = false
      handleErrors(payload)
    },
    [updatePermitType.pending.type]: (state) => {
      state.permitTypes.isLoading = true
    },
    [updatePermitType.fulfilled.type]: (state) => {
      state.permitTypes.isLoading = false
      toast.success('Permit type updated successfully')
    },
    [updatePermitType.rejected.type]: (state, { payload }) => {
      state.permitTypes.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearPermits, clearSinglePermit, clearPermitTypes, clearSinglePermitType } =
  permitsSlice.actions

export default permitsSlice.reducer
